import { iconNavBusca, iconNavFechar } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsInput } from '@bsds-lib-component/react';
import React, { useId, useMemo, useState } from 'react';

import { CoveragesTable, Dropdown, SystemMessage, useSystemContainer, Visibility } from '../../../../components';
import Button from '../../../../components/button/Button';
import { useVidaContract } from '../../../../hooks';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import { useCompanyStore } from '../../../../store/companyStore';
import type { ContractCoveragesResponse, CoverageItem } from '../../../../types';
import { formatCnpj } from '../../../../utils/cnpjUtils';
import { formatCpf } from '../../../../utils/cpfUtils';
import { subestipulanteDropdownOptions } from '../../../../utils/subestipulanteUtil';
import { formatZipCode } from '../../../../utils/zipCodeUtils';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

// Helper function to format percentage
const formatPercentage = (value: number): string => {
  if (value === 0 || !value) return '-';
  return `${(value * 100).toFixed(0)}%`;
};

// Helper function to format date from yyyy-MM-dd to dd/MM/yyyy
const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

/** Visual-only: CNPJ (14 alphanumeric), CPF (11 digits) or RG (7–9 digits) */
const formatDocumentNumber = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  const alnum = raw.replace(/[^a-zA-Z0-9]/g, '');
  if (alnum.length === 14) return formatCnpj(alnum);
  if (/^\d{11}$/.test(alnum)) return formatCpf(alnum);

  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 7 && digits.length <= 9) {
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4')
      .slice(0, 12);
  }
  return raw;
};

// Helper function to map waiting type
const mapWaitingType = (type: string): string => {
  if (!type || type === 'NONE') return 'Dias';
  const typeMap: Record<string, string> = {
    DIAS: 'Dias',
    MESES: 'Meses',
    ANOS: 'Anos',
  };
  return typeMap[type] || type;
};

// Helper function to map deductible type
const mapDeductibleType = (type: string): string => {
  const typeMap: Record<string, string> = {
    NAO_APLICAVEL: '-',
    VALOR_FIXO: 'Valor Fixo',
    PERCENTUAL: 'Percentual',
  };
  return typeMap[type] || type;
};

// Required params for all VIDA_CONTRACTS requests: identContrato, identSubestipulante, identContratoEst; identAbertoPorCoberetura defaults to 'S'

// Helper function to map main insured coverages (Death and Permanent Disability)
const mapMainInsuredCoverages = (coverages: ContractCoveragesResponse | undefined): CoverageItem[] => {
  if (!coverages) return [];

  const items: CoverageItem[] = [];
  let idCounter = 1;

  // Death coverage
  if (coverages.deathFixedCapital && coverages.deathFixedCapital > 0) {
    items.push({
      id: idCounter++,
      coverage: 'Morte',
      basicPercentage: formatPercentage(coverages.deathPercentageBasic),
      capitalLimit: coverages.deathCapitalLimit && coverages.deathCapitalLimit > 0 ? formatCurrency(coverages.deathCapitalLimit) : formatCurrency(coverages.deathFixedCapital),
      fixedCapital: formatCurrency(coverages.deathFixedCapital),
      quantity: coverages.deathQuantity?.toString() || '1',
      premium: formatCurrency(coverages.deathPremium),
      gracePeriod: coverages.deathWaitingPeriod?.toString() || '0',
      gracePeriodType: mapWaitingType(coverages.deathWaitingType),
      franchise: coverages.deathDeductible && coverages.deathDeductible > 0 ? formatCurrency(coverages.deathDeductible) : '-',
      franchiseType: mapDeductibleType(coverages.deathDeductibleType),
    });
  }

  // Permanent Disability by Accident coverage
  if (coverages.permanentDisabilityFixedCapital && coverages.permanentDisabilityFixedCapital > 0) {
    items.push({
      id: idCounter++,
      coverage: 'Invalidez Permanente por Acidente',
      basicPercentage: formatPercentage(coverages.permanentDisabilityPercentageBasic),
      capitalLimit: coverages.permanentDisabilityCapitalLimit && coverages.permanentDisabilityCapitalLimit > 0 ? formatCurrency(coverages.permanentDisabilityCapitalLimit) : formatCurrency(coverages.permanentDisabilityFixedCapital),
      fixedCapital: formatCurrency(coverages.permanentDisabilityFixedCapital),
      quantity: coverages.permanentDisabilityQuantity?.toString() || '1',
      premium: formatCurrency(coverages.permanentDisabilityPremium),
      gracePeriod: coverages.permanentDisabilityWaitingPeriod?.toString() || '0',
      gracePeriodType: mapWaitingType(coverages.permanentDisabilityWaitingType),
      franchise: coverages.permanentDisabilityDeductible && coverages.permanentDisabilityDeductible > 0 ? formatCurrency(coverages.permanentDisabilityDeductible) : '-',
      franchiseType: mapDeductibleType(coverages.permanentDisabilityDeductibleType),
    });
  }

  return items;
};

// Helper function to map assistances (Funeral Aid)
const mapAssistances = (coverages: ContractCoveragesResponse | undefined): CoverageItem[] => {
  if (!coverages) return [];

  const items: CoverageItem[] = [];

  // Funeral Aid coverage (assistance)
  if (coverages.funeralAidFixedCapital && coverages.funeralAidFixedCapital > 0) {
    items.push({
      id: 1,
      coverage: 'Auxílio Funeral',
      basicPercentage: formatPercentage(coverages.funeralAidPercentageBasic),
      capitalLimit: coverages.funeralAidCapitalLimit && coverages.funeralAidCapitalLimit > 0 ? formatCurrency(coverages.funeralAidCapitalLimit) : formatCurrency(coverages.funeralAidFixedCapital),
      fixedCapital: formatCurrency(coverages.funeralAidFixedCapital),
      quantity: coverages.funeralAidQuantity?.toString() || '1',
      premium: formatCurrency(coverages.funeralAidPremium),
      gracePeriod: coverages.funeralAidWaitingPeriod?.toString() || '0',
      gracePeriodType: mapWaitingType(coverages.funeralAidWaitingType),
      franchise: coverages.funeralAidDeductible && coverages.funeralAidDeductible > 0 ? formatCurrency(coverages.funeralAidDeductible) : '-',
      franchiseType: mapDeductibleType(coverages.funeralAidDeductibleType),
    });
  }

  return items;
};

const ContractTab: React.FC = () => {
  const selectedCompany = useCompanyStore((s) => s.selectedCompany);
  const policyNumeroApolice = selectedCompany?.policySelection?.numeroApolice?.trim() ?? '';
  const policyIdentContrato = (() => {
    const ctx = selectedCompany?.apoliceContext;
    const raw = ctx?.identContratoApol ?? ctx?.identContrato;
    if (raw == null) return '';
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? String(n) : '';
  })();

  // Hook to load contract details
  const { isLoading, clearError, contractData, loadContractDetails } = useVidaContract();
  const { showMessage, message, type, show } = useSystemMessage();
  const { setLoading } = useSystemContainer();
  const mainContentId = useId();
  const searchFormId = useId();
  const resultsId = useId();
  const emptyStateId = useId();
  const messageId = useId();
  const subestipulanteSelectId = useId();

  const [subStipulatorId, setSubStipulatorId] = useState('');

  const subestipulanteOptions = useMemo(
    () => subestipulanteDropdownOptions(selectedCompany?.subestipulantes, false),
    [selectedCompany?.subestipulantes]
  );
  // Optional / extra search fields
  const [branch, setBranch] = useState('');

  // State to control if there are search results
  const [hasSearchResults, setHasSearchResults] = useState<boolean>(false);
  // State to track if a search was performed
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const parsedContractId = policyIdentContrato ? parseInt(policyIdentContrato, 10) : NaN;
  const parsedSubStipulatorId = subStipulatorId.trim() ? parseInt(subStipulatorId, 10) : NaN;
  const hasValidContractId = !Number.isNaN(parsedContractId) && parsedContractId > 0;
  const hasValidSubStipulatorId = !Number.isNaN(parsedSubStipulatorId) && parsedSubStipulatorId > 0;
  const isSearchEnabled = hasValidContractId && hasValidSubStipulatorId;

  const handleSearch = async () => {
    if (!isSearchEnabled) {
      clearError();
      showMessage('Preencha todos os campos obrigatórios (Identidade Contrato e Subestipulante/Identificador Subestipulante) com números válidos', 'warning');
      return;
    }

    clearError();
    setHasSearchResults(false);
    setHasSearched(false);

    const numApoliceEst = policyNumeroApolice ? parseInt(policyNumeroApolice, 10) : undefined;
    const codSucursalEst = branch.trim() ? parseInt(branch, 10) : undefined;

    try {
      await loadContractDetails({
        identContrato: parsedContractId,
        identSubestipulante: parsedSubStipulatorId,
        identContratoEst: parsedContractId,
        identAbertoPorCoberetura: 'S',
        numApoliceEst: numApoliceEst !== undefined && !Number.isNaN(numApoliceEst) ? numApoliceEst : undefined,
        codSucursalEst: codSucursalEst !== undefined && !Number.isNaN(codSucursalEst) ? codSucursalEst : undefined,
      });
      showMessage('Contrato encontrado e detalhes carregados com sucesso', 'success');
      setHasSearchResults(true);
      setHasSearched(true);
    } catch (err) {
      console.error('Erro ao carregar detalhes do contrato:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar detalhes do contrato';
      showMessage(errorMsg, 'error');
      setHasSearchResults(false);
      setHasSearched(true);
    }
  };

  const handleClearFilters = () => {
    setSubStipulatorId('');
    setBranch('');
    setHasSearchResults(false);
    setHasSearched(false);
    clearError();
  };

  const handleInputChange = (setter: (value: string) => void) => (
    event: React.FormEvent<HTMLBsdsInputElement>
  ) => {
    const target = event.target as HTMLInputElement;
    setter(target?.value || '');
  };

  React.useEffect(() => {
    const allowed = new Set(subestipulanteOptions.map((option) => option.value));
    if (subStipulatorId && !allowed.has(subStipulatorId)) {
      setSubStipulatorId('');
    }
  }, [subestipulanteOptions, subStipulatorId]);

  React.useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-busy={isLoading}
      aria-label="Consulta de apólices"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mb-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage
            message={message}
            type={type}
            show={show}
          />
        </div>
      )}
      {/* Header */}
      <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
        <div className="bsds-display-flex bsds-align-items-center">
          <div className="w-[2.5px] h-8  bg-red-600 bsds-mr-8" aria-hidden="true" />
        </div>
        <h5 className="bsds-ml-8">Consulta de Apólices</h5>
      </div>

      {/* Search Form */}
      <div
        id={searchFormId}
        className="bsds-mb-24 bsds-mt-24"
        role="region"
        aria-label="Formulário de busca de apólices"
      >
        <div className="bsds-mb-16">
          {/* Field grid - Sub-Estipulante (2/3) + Apólice on first row, Sucursal below */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-mb-16">
            <div className="md:col-span-2">
              <Dropdown
                id={subestipulanteSelectId}
                label="Sub-Estipulante *"
                placeholder="Selecione um Sub-Estipulante"
                value={subStipulatorId}
                onChange={setSubStipulatorId}
                options={subestipulanteOptions}
              />
            </div>

            {/* Policy Number (optional: numApoliceEst) */}
            <BsdsInput
              value={policyNumeroApolice}
              label="Número da Apólice"
              type="text"
              inputMode="numeric"
              color="primary"
              platform="web"
              className="bsds-width-100"
              readonly
              aria-label="Número da apólice selecionada"
              aria-readonly="true"
            />

            {/* Branch (codSucursalEst - optional int) */}
            <BsdsInput
              value={branch}
              onInput={handleInputChange(setBranch)}
              label="Sucursal"
              placeholder="Ex.: 5317"
              type="text"
              inputMode="numeric"
              color="primary"
              platform="web"
              className="bsds-width-100"
              aria-label="Código sucursal (opcional)"
            />
          </div>

          {/* Buttons */}
          <div
            className="bsds-display-flex bsds-justify-content-end gap-4"
            role="group"
            aria-label="Ações do formulário de busca"
          >
            <Button
              onClick={handleClearFilters}
              color="secondary"
              fill="outline"
              icon={iconNavFechar}
              iconPosition="end"
              aria-label="Limpar todos os filtros do formulário"
            >
              Limpar Filtros
            </Button>
            <Button
              onClick={handleSearch}
              color="primary"
              fill="solid"
              loading={isLoading}
              disabled={isLoading || !isSearchEnabled}
              icon={iconNavBusca}
              iconPosition="start"
              aria-label={
                isLoading
                  ? 'Pesquisando apólice...'
                  : !isSearchEnabled
                    ? 'Preencha Identidade Contrato e Subestipulante/Identificador Subestipulante para pesquisar'
                    : 'Pesquisar apólice'
              }
            >
              Pesquisar
            </Button>
          </div>
        </div>
      </div>

      {/* Results content - visible only when there is data */}
      <Visibility isActive={hasSearchResults && !!contractData} animated animationDuration={300}>
        <div
          id={resultsId}
          className="bsds-mb-16 bsds-mt-34"
          role="region"
          aria-label="Resultados da busca de apólice"
        >
          {/* Contract */}
          <div role="region" aria-label="Informações do contrato">
            <BsdsCard className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-p-16 bg-gray-100/30">
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número do Contrato</span>
                <span className="bsds-text-regular font-semibold" aria-label={`Número do contrato: ${contractData?.header?.contractNumber || policyIdentContrato || '-'}`}>
                  {contractData?.header?.contractNumber || policyIdentContrato || '-'}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Produto</span>
                <span className="bsds-text-regular" aria-label={`Produto: $
                  {contractData?.header?.subProductName || '-'}`}>
                  {contractData?.header?.subProductCode} - {contractData?.header?.subProductName.slice(0, 45)}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Estipulante</span>
                <span className="bsds-text-regular" aria-label={`Estipulante: ${contractData?.holderData?.corporateName || '-'}`}>
                  {contractData?.holderData?.corporateName || '-'}
                </span>
              </div>
            </BsdsCard>
          </div>

          {/* Stipulator Data */}
          <BsdsCard className="bsds-mt-16 bsds-p-16" role="region" aria-label="Dados do estipulante">
            <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
              <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h4 className="opacity-80">Dados do Estipulante</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="order-1 md:order-none md:col-start-1 md:row-start-1">
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Razão Social</span>
                <span className="bsds-text-regular">{contractData?.holderData?.corporateName || '-'}</span>
              </div>
              <div className="order-2 md:order-none md:col-start-2 md:row-start-1">
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Nome Fantasia</span>
                <span className="bsds-text-regular">{contractData?.holderData?.tradeName || '-'}</span>
              </div>
              <div className="order-3 md:order-none md:col-start-3 md:row-start-1">
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número do Documento</span>
                <span className="bsds-text-regular">{formatDocumentNumber(contractData?.holderData?.documentNumber) || '-'}</span>
              </div>
              <div className="order-4 md:order-none md:col-start-1 md:row-start-2">
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Início da Vigência</span>
                <span className="bsds-text-regular">{contractData?.holderData?.validityStart ? formatDate(contractData.holderData.validityStart) : '-'}</span>
              </div>
              <div className="order-5 md:order-none md:col-start-2 md:row-start-2">
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Fim da Vigência</span>
                <span className="bsds-text-regular">{contractData?.holderData?.validityEnd ? formatDate(contractData.holderData.validityEnd) : '-'}</span>
              </div>
            </div>
          </BsdsCard>

          {/* Stipulator Address */}
          <BsdsCard className="bsds-mt-16 bsds-p-16" role="region" aria-label="Endereço do estipulante">
            <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
              <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h4 className="opacity-80">Endereço do Estipulante</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">CEP</span>
                <span className="bsds-text-regular">{formatZipCode(contractData?.holderAddress?.zipCode) || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Bairro</span>
                <span className="bsds-text-regular">{contractData?.holderAddress?.district || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Endereço</span>
                <span className="bsds-text-regular">{contractData?.holderAddress?.street || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Cidade</span>
                <span className="bsds-text-regular">{contractData?.holderAddress?.city || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Estado</span>
                <span className="bsds-text-regular">{contractData?.holderAddress?.state || '-'}</span>
              </div>
            </div>
          </BsdsCard>

          {/* Main Insured */}
          <BsdsCard className="bsds-mt-16 bsds-p-16" role="region" aria-label="Coberturas do Segurado principal">
            <div className="bsds-display-flex bsds-align-items-center bsds-mb-12">
              <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h4 className="opacity-80">Segurado Principal</h4>
            </div>

            {/* Coverages Table */}

            <CoveragesTable
              coverages={mapMainInsuredCoverages(contractData?.coverages)}
              emptyMessage="Nenhuma cobertura encontrada"
            />
          </BsdsCard>

          {/* Assistances */}
          <BsdsCard className="bsds-mt-16 bsds-p-16" role="region" aria-label="Assistências do contrato">
            <div className="bsds-display-flex bsds-align-items-center bsds-mb-12">
              <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h4 className="opacity-80">Assistências</h4>
            </div>
            {/* Assistances Table */}
            <CoveragesTable
              coverages={mapAssistances(contractData?.coverages)}
              emptyMessage="Nenhuma assistência encontrada"
            />
          </BsdsCard>

          {/* Age Limit and Insured Capital */}
          <BsdsCard className="bsds-mt-16 bsds-p-16" role="region" aria-label="Limites de idade do contrato">
            <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
              <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h4 className="opacity-80">Limite de Idade</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Idade Mínima</span>
                <span className="bsds-text-regular bsds-text-lg">{contractData?.coverages?.minimumAge ? `${contractData.coverages.minimumAge} anos` : '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Limite de Idade para 1ª Fatura</span>
                <span className="bsds-text-regular bsds-text-lg">{contractData?.coverages?.maxAgeFirstInvoice ? `${contractData.coverages.maxAgeFirstInvoice} anos` : '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Idade Máxima Demais Faturas</span>
                <span className="bsds-text-regular bsds-text-lg">{contractData?.coverages?.maxAgeOtherInvoices ? `${contractData.coverages.maxAgeOtherInvoices} anos` : '-'}</span>
              </div>
            </div>
          </BsdsCard>


          <BsdsCard className="bsds-mt-16 bsds-p-16" role="region" aria-label="Capital Segurado do contrato">
            <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
              <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h4 className="opacity-80">Capital Segurado</h4>
            </div>
            <div>
              <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Valor do Capital</span>
              <span className="bsds-text-regular bsds-text-lg">
                {contractData?.coverages?.capitalBaseValue ? formatCurrency(contractData.coverages.capitalBaseValue) : '-'}
              </span>
            </div>
          </BsdsCard>
        </div>
      </Visibility>

      <Visibility isActive={hasSearched && !hasSearchResults} animated animationDuration={300}>
        <div
          id={emptyStateId}
          className="bsds-display-flex bsds-justify-content-center bsds-align-items-center bsds-mt-64 bsds-mb-64"
          role="status"
          aria-live="polite"
          aria-label="Resultado da busca"
        >
          <div className="bsds-text-center">
            <p className="bsds-text-regular bsds-text-lg text-gray-600">
              Dados não encontrados para a pesquisa realizada
            </p>
          </div>
        </div>
      </Visibility>
    </div>
  );
};

export default ContractTab;

